import React, { Component } from 'react';
import { get, isEmpty, max, range, isEqual, isNil } from 'lodash-es';
import classNames from 'classnames';

interface ItemProps {
	rowInitialNumber: number;
	rowActualIndex: number;
	rowName: string;
	currentListLength: number;
	removeRow: (index: number) => void;
	getItemName: (itemId: string) => string;
	getInitValue: (propName: string | string[]) => { [key: string]: any } | undefined;
}

interface Props {
	listName: string;
	max?: number;
	min: number;
	className?: string;
	allowEmply?: boolean;
	initialValues?: Array<{ [key: string]: any }>;
	render: (props: ItemProps) => React.ReactNode;
	renderAddButton?: (addRow: () => void, isMaxRows: boolean) => React.ReactNode;
	validateFields?: () => void;
	updateRangeCallback?: (listRange: number[]) => void;
}

interface State {
	listRange: number[];
}

class DynamicFields extends Component<Props, State> {
	private globalLatestRowIndex: number;

	static defaultProps = {
		min: 1,
	};

	constructor(props: Props) {
		super(props);
		const initialRange = this.getRange();
		this.state = {
			listRange: initialRange,
		};
		this.globalLatestRowIndex = max(initialRange) || 0;
	}

	componentDidUpdate(prevProps: Props) {
		if (!isEqual(prevProps.initialValues, this.props.initialValues)) {
			this.setState({
				listRange: this.getRange(),
			});
		}
	}

	addRow = () => {
		const { max } = this.props;
		const keys = this.state.listRange;
		if (max && max === keys.length) {
			return;
		}
		this.globalLatestRowIndex = this.globalLatestRowIndex + 1;
		const newRange = [...keys, this.globalLatestRowIndex];
		this.updateRange(newRange);
	};

	removeRow = (rowIndex: number) => {
		const keys = this.state.listRange;
		if (keys.length === this.props.min) {
			return;
		}
		const newRange = keys.filter(item => item !== rowIndex);
		this.updateRange(newRange);
	};

	updateRange = (listRange: number[]) => {
		this.setState(
			{
				listRange,
			},
			this.props.validateFields
		);
		if (this.props.updateRangeCallback) {
			this.props.updateRangeCallback(listRange);
		}
	};

	getRange = (): number[] => {
		let listRange = this.props.allowEmply ? [] : [0];
		const { initialValues } = this.props;
		if (!isEmpty(initialValues)) {
			if (initialValues && !isNil(initialValues[0].rowInitialNumber)) {
				listRange = initialValues.map(item => item.rowInitialNumber);
			} else {
				listRange = range(0, initialValues!.length);
			}
		}
		return listRange;
	};

	getItemName = (listName: string, rowIndex: number) => {
		return (itemName: string) => {
			return `${listName}[${rowIndex}][${itemName}]`;
		};
	};

	getInitValue = (index: number) => (
		propName: string | string[]
	): { [key: string]: unknown } | undefined => {
		const { initialValues } = this.props;
		if (!initialValues) {
			return;
		}
		if (!Array.isArray(propName)) {
			return get(initialValues, `[${index}].${propName}`);
		}
		const value = get(initialValues, `[${index}]`, {});

		// case in AlternativeContacts
		return propName.reduce((prev, k: string) => {
			const v = value[k];
			return {
				...prev,
				[k]: v,
			};
		}, {});
	};

	addMultipleFields = () => {
		const { listName, render } = this.props;
		const { listRange } = this.state;
		return listRange.map((rowInitialNumber, rowActualIndex) => {
			const rowName = `${listName}[${rowInitialNumber}]`;
			const getItemName = this.getItemName(listName, rowInitialNumber);
			const getInitValue = this.getInitValue(rowInitialNumber);
			const currentListLength = listRange.length;
			return render({
				rowName,
				getItemName,
				rowInitialNumber,
				rowActualIndex,
				removeRow: this.removeRow,
				getInitValue,
				currentListLength,
			});
		});
	};

	isMaxRows = () => {
		return this.state.listRange.length === this.props.max;
	};

	render() {
		const { renderAddButton } = this.props;
		return (
			<div className={classNames(this.props.className)}>
				{this.addMultipleFields()}
				{renderAddButton && renderAddButton(this.addRow, this.isMaxRows())}
			</div>
		);
	}
}

export default DynamicFields;
